// Estado del TPV (tiempo real + worker + impresora) compartido por todas las
// pastillas de la página (TopBar, cabecera de la carta, flotante). Una sola
// evaluación por segundo aunque haya varias pastillas montadas (los dos
// layouts pueden solaparse en una transición): `realtime.lost` /
// `realtime.restored` y los toasts salen una sola vez por corte.
// La lógica pura está en staleStatus.js (tiempo real) y tpvStatus.js.
import { getConnectionStates, reconnectNow } from "src/utils/hubs/HubConnectionManager";
import journal from "src/utils/journal/journal";
import { showSnackbar } from "src/utils/globalSnackbar";
import { computeIssuesFingerprint, getIssuePrinters } from "src/layouts/DashboardLayout/printerIssues";
import { getResumeHealth, hasRealtimeCatchUp, requestResume, subscribeResumeHealth } from "./tpvResume";
import { createRealtimeNotice } from "./staleStatus";
import { isRealtimeV2Enabled } from "./realtimeFlag";
import {
  TOAST_HIDE_MS,
  buildStatusItems,
  createStatusToaster,
  createWorkerOfflineDebounce,
  pickWorstStatus,
} from "./tpvStatus";

const POLL_MS = 1000;
const V2 = isRealtimeV2Enabled();
// Hub del que depende ver el estado del local: con V2 todo va por `/hub/tpv`;
// en legacy, mesas (y tpv si estuviera abierto).
const CRITICAL_HUB = V2 ? /\/hub\/(tpv)(\?|$)/i : /\/hub\/(tpv|mesahub)(\?|$)/i;

const hubNameOf = (url) => (CRITICAL_HUB.exec(url || "") || [])[1]?.toLowerCase() || null;

/** Callbacks de la máquina → diario del TPV. */
export const noticeJournal = (track) => ({
  onLost: ({ hub, reason }) => track("realtime.lost", { hub, reason }),
  onRestored: ({ hub, downMs, caughtUp }) => track("realtime.restored", { hub, downMs, caughtUp }),
});

const toast = (message, variant) =>
  showSnackbar(message, {
    variant,
    autoHideDuration: TOAST_HIDE_MS,
    preventDuplicate: true,
    anchorOrigin: { vertical: "bottom", horizontal: "center" },
  });

const EMPTY = { items: [], worst: null, offlineWorkers: [], printerIssues: [], printerFingerprint: null };

let notice = null;
let workerDebounce = null;
let toaster = null;
let workerStatus = null; // state.workerStatus, lo aporta useTpvStatus
let snapshot = EMPTY;
const listeners = new Set();
let running = 0;
let stopRunning = null;
let generation = 0; // un reset invalida los stop() pendientes
let embedded = 0;
const embeddedListeners = new Set();

const ensureMachines = () => {
  if (!notice) notice = createRealtimeNotice(noticeJournal(journal.track));
  if (!workerDebounce) workerDebounce = createWorkerOfflineDebounce();
  if (!toaster) toaster = createStatusToaster({ notify: toast });
};

const sameSnapshot = (a, b) =>
  a.items.length === b.items.length
  && a.printerFingerprint === b.printerFingerprint
  && a.items.every((it, i) =>
    it.key === b.items[i].key
    && it.short === b.items[i].short
    && it.detail === b.items[i].detail);

/** Evalúa los tres subsistemas y avisa a las pastillas si algo cambió. */
export function evaluateTpvStatus(now = Date.now()) {
  ensureMachines();
  const critical = getConnectionStates().filter((c) => CRITICAL_HUB.test(c.url || ""));
  const down = critical.find((c) => c.state !== "Connected");
  const health = getResumeHealth();
  const realtime = notice.update(
    {
      online: typeof navigator === "undefined" ? true : navigator.onLine,
      health,
      hubDown: !!down,
      hub: hubNameOf(down?.url || critical[0]?.url) || (V2 ? "tpv" : "mesahub"),
      catchUpAvailable: hasRealtimeCatchUp(),
      lastCaughtUpAt: health?.lastCaughtUpAt ?? null,
    },
    now,
  );

  const workers = workerStatus?.workers || [];
  const offlineWorkers = workers.filter((w) => !w.isOnline);
  const workerDown = workerDebounce.update(offlineWorkers.length > 0, now);
  // Respeta el «Ocultar aviso» de la impresora: si el conjunto de incidencias
  // es el mismo que se ocultó, no cuenta (reaparece si cambia).
  const issues = getIssuePrinters(workers);
  const fingerprint = issues.length ? computeIssuesFingerprint(issues) : null;
  const printerIssues = fingerprint && fingerprint !== workerStatus?.dismissedPrinterIssuesFingerprint ? issues : [];

  const items = buildStatusItems({ realtime, workerDown, offlineWorkers, printerIssues });
  toaster.update({ items, realtimeKind: realtime?.kind || null }, now);

  const next = { items, worst: pickWorstStatus(items), offlineWorkers, printerIssues, printerFingerprint: fingerprint };
  if (!sameSnapshot(snapshot, next)) {
    snapshot = next;
    listeners.forEach((fn) => {
      try {
        fn(snapshot);
      } catch {
        /* noop */
      }
    });
  }
  return snapshot;
}

export const getTpvStatus = () => snapshot;

export function subscribeTpvStatus(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Último `state.workerStatus` (Redux) visto por una pastilla. */
export function setWorkerStatusInput(next) {
  if (next === workerStatus) return;
  workerStatus = next;
  if (running) evaluateTpvStatus();
}

/** Arranca el sondeo (con refcount: una sola evaluación aunque haya varias pastillas). */
export function startTpvStatus() {
  running += 1;
  if (running === 1) {
    const evaluate = () => evaluateTpvStatus();
    evaluate();
    const t = setInterval(evaluate, POLL_MS);
    const off = subscribeResumeHealth(evaluate);
    window.addEventListener("online", evaluate);
    window.addEventListener("offline", evaluate);
    stopRunning = () => {
      clearInterval(t);
      off();
      window.removeEventListener("online", evaluate);
      window.removeEventListener("offline", evaluate);
    };
  }
  let stopped = false;
  const gen = generation;
  return () => {
    if (stopped || gen !== generation) return;
    stopped = true;
    running -= 1;
    if (running === 0 && stopRunning) {
      stopRunning();
      stopRunning = null;
    }
  };
}

/**
 * «Reintentar» del tiempo real: intento inmediato de los hubs caídos o
 * reconectando (los Reconnecting incluidos: refreshStaleConnections no los
 * tocaba y el botón no hacía nada) y nueva vuelta / puesta al día.
 */
export function retryRealtime() {
  reconnectNow("manual");
  requestResume("manual");
}

// Pastilla dentro de una cabecera (carta): la flotante del layout se oculta
// mientras haya alguna montada, para no enseñar dos.
export function registerEmbeddedPill() {
  embedded += 1;
  embeddedListeners.forEach((fn) => fn(embedded));
  let done = false;
  const gen = generation;
  return () => {
    if (done || gen !== generation) return;
    done = true;
    embedded -= 1;
    embeddedListeners.forEach((fn) => fn(embedded));
  };
}

export const getEmbeddedPillCount = () => embedded;

export function subscribeEmbeddedPills(fn) {
  embeddedListeners.add(fn);
  return () => embeddedListeners.delete(fn);
}

/** Solo tests: estado limpio. */
export function __resetTpvStatusForTests() {
  generation += 1;
  if (stopRunning) stopRunning();
  stopRunning = null;
  running = 0;
  notice = null;
  workerDebounce = null;
  toaster = null;
  workerStatus = null;
  snapshot = EMPTY;
  listeners.clear();
  embedded = 0;
  embeddedListeners.clear();
}
